import {useMutation, UseMutationOptions, useQueryClient, InfiniteData} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {httpSendConversationMessage, SendMessagePayload} from '../http/conversations.http'
import {ConversationMessage, ConversationMessagesList} from '@/features/conversations/types.ts'
import {QUERY_KEYS} from '@/queryClient.ts'
import {parseAxiosPromise} from '@/axiosInstance.ts'
import {captureException} from '@sentry/react'
import {errorHandler} from '@/utilities/helpers'

export const useSendConversationMessage = (
    options?: UseMutationOptions<ConversationMessage, AxiosError, SendMessagePayload>
) => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (payload: SendMessagePayload) =>
            parseAxiosPromise({
                axiosPromise: httpSendConversationMessage(payload),
                onZodError: captureException,
                responseShape: ConversationMessage
            }),
        onSuccess: (data, variables, context) => {
            // Optimistically add the new message to the cache
            queryClient.setQueryData(
                [QUERY_KEYS.CONVERSATION_MESSAGES, variables.conversationId, 30],
                (oldData: InfiniteData<ConversationMessagesList> | undefined) => {
                    if (!oldData) return oldData

                    const updatedPages = [...oldData.pages]
                    if (updatedPages.length > 0) {
                        // pages[0] is page 1, the most recent page; append the new message
                        // to its end since it is now the newest message in the conversation.
                        const mostRecentPageIndex = 0
                        updatedPages[mostRecentPageIndex] = {
                            ...updatedPages[mostRecentPageIndex],
                            data: [...updatedPages[mostRecentPageIndex].data, data],
                            meta: {
                                ...updatedPages[mostRecentPageIndex].meta,
                                total: updatedPages[mostRecentPageIndex].meta.total + 1
                            }
                        }
                    }

                    return {
                        ...oldData,
                        pages: updatedPages
                    }
                }
            )

            void queryClient.invalidateQueries({queryKey: [QUERY_KEYS.CONVERSATIONS, data.sender.id]})

            // Call original onSuccess if provided
            options?.onSuccess?.(data, variables, context)
        },
        onError: (error, variables, context) => {
            errorHandler(error)
            options?.onError?.(error, variables, context)
        },
        ...options
    })
}
